import * as React from "react";

import { cn } from "~/lib/utils";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  "data-testid"?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, "data-testid": dataTestId, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        data-testid={dataTestId || "Table"}
        {...props}
      />
    </div>
  ),
);
Table.displayName = "Table";

interface TableHeaderProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  "data-testid"?: string;
}

const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, "data-testid": dataTestId, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn("[&_tr]:border-b", className)}
      data-testid={dataTestId || "TableHeader"}
      {...props}
    />
  ),
);
TableHeader.displayName = "TableHeader";

interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  "data-testid"?: string;
}

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, "data-testid": dataTestId, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn("[&_tr:last-child]:border-0", className)}
      data-testid={dataTestId || "TableBody"}
      {...props}
    />
  ),
);
TableBody.displayName = "TableBody";

interface TableFooterProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  "data-testid"?: string;
}

const TableFooter = React.forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, "data-testid": dataTestId, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className,
      )}
      data-testid={dataTestId || "TableFooter"}
      {...props}
    />
  ),
);
TableFooter.displayName = "TableFooter";

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-testid"?: string;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, "data-testid": dataTestId, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      data-testid={dataTestId || "TableRow"}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  className?: string;
  "data-testid"?: string;
};

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, "data-testid": dataTestId, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className,
      )}
      data-testid={dataTestId || "TableHead"}
      {...props}
    />
  ),
);

TableHead.displayName = "TableHead";

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  className?: string;
  "data-testid"?: string | undefined;
};

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, "data-testid": dataTestId, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        "p-4 align-middle [&:has([role=checkbox])]:pr-0",
        className,
      )}
      data-testid={dataTestId || "TableCell"}
      {...props}
    />
  ),
);
TableCell.displayName = "TableCell";

interface TableCaptionProps
  extends React.HTMLAttributes<HTMLTableCaptionElement> {
  "data-testid"?: string;
}

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  TableCaptionProps
>(({ className, "data-testid": dataTestId, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    data-testid={dataTestId || "TableCaption"}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
